import { clamp } from '../math';
import type { World } from '../World';

/* ═══════════════════════════════════════════════════════════════════════════
   LE RANG CACHÉ — difficulté dynamique et INVISIBLE (PLAN.md §6.10).

   Le constat qui l'appelle, mot pour mot d'un retour de jeu : « même au dernier
   monde je me retrouve à attendre, c'est trop facile ». Un joueur qui domine ne
   trouve plus de résistance ; un joueur qui souffre s'enfonce. Le rang corrige
   les deux, sans jamais s'afficher — c'est un curseur, pas un compteur.

   ── Ce qu'il lit, ce qu'il écrit ─────────────────────────────────────────────
   Il lit les indicateurs de performance déjà tenus par le monde — kills,
   `grazeCount`, `progress.hitsTaken`, `progress.deaths` — et n'écrit qu'un
   scalaire borné, `run.rankMul`. Il ne touche RIEN d'autre.

   ⚠️ Ce que `rankMul` DURCIT est décidé par la couche qui le lit, pas ici. Le
   plan §6.10 est explicite (et `difficulty.ts` le rappelle) : « le rank ne
   touche jamais les PV ni les récompenses ». L'intégration s'y tient donc — le
   rang est branché sur la CADENCE de tir ennemie (`emitterSystem`), un levier de
   PRESSION, et jamais sur les PV ni le score. Deux scores restent comparables ;
   ce qui change, c'est la densité du rideau, pas le mur de vie. Le rang, lui,
   reste agnostique : il produit un multiplicateur, un seul point de lecture
   décide de son effet.

   ── Pourquoi une VARIATION et non un total ───────────────────────────────────
   `rankPerf` est la performance cumulée pondérée au tick précédent ; on ne
   travaille que sur son DELTA. Un total absolu deviendrait sourd en fin de
   partie — après cinq cents kills, une passe catastrophique ne pèserait plus
   rien dans la somme, et le rang ne redescendrait jamais. Le delta reste vif à
   toute heure : les derniers événements comptent autant au monde 3 qu'au monde 1.

   Comme `rankMul` est borné à CHAQUE tick alors que `rankPerf` ne l'est pas, la
   saturation ne « s'accumule » pas : un joueur qui domine à fond plafonne à
   `RANK_MAX`, et dès qu'il encaisse, le rang REDESCEND immédiatement depuis le
   plafond — pas de retard d'anti-emballement à rattraper.

   ── Déterminisme ─────────────────────────────────────────────────────────────
   Arithmétique pure sur des compteurs entiers : aucun `Math.random`, aucune
   fonction transcendante, aucun `**`. Deux mêmes suites d'états donnent le même
   `rankMul`, ce qui le rend rejouable et vérifiable comme le reste de la sim.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Bornes du multiplicateur. En deçà de 1, une VRAIE clémence (ennemis à 85 %
 *  de leurs PV) pour sortir un joueur d'une spirale ; au-delà, jusqu'à +60 % de
 *  PV pour qui écrase tout. La plage est volontairement asymétrique autour de 1 :
 *  on durcit davantage qu'on n'adoucit, parce que le défaut à corriger est « trop
 *  facile », pas « trop dur ». */
export const RANK_MIN = 0.85;
export const RANK_MAX = 1.6;

/* Contributions par événement, exprimées DIRECTEMENT en unités de `rankMul`.
   Elles sont minuscules par dessein : le rang doit bouger LENTEMENT, sur des
   dizaines de secondes, jamais réagir à un tick isolé — un joueur ne doit pas
   pouvoir sentir la marche du curseur.

   Calibrage : ~460 kills sans dommage font le trajet de 1,0 à RANK_MAX ; un coup
   encaissé efface une vingtaine de kills, une mort en efface une centaine. La
   punition domine la récompense, pour que la spirale de la souffrance se casse
   vite et que la montée, elle, se mérite. */
const UP_PER_KILL = 0.0013;
const UP_PER_GRAZE = 0.0003;
const DOWN_PER_HIT = 0.03;
const DOWN_PER_DEATH = 0.15;

/**
 * Un pas du rang caché. À appeler une fois par tick, en fin de chaîne : il lit
 * les compteurs de performance FINALISÉS du tick et écrit `run.rankMul` pour le
 * peuplement du tick suivant. Il ne dépend d'aucun ordre entre systèmes de
 * gameplay — seulement de compteurs déjà à jour.
 */
export function rankSystem(w: World): void {
  const run = w.run;

  /* Performance cumulée, pondérée et signée. Les termes positifs récompensent la
     DOMINATION (détruire, frôler avec maîtrise), les négatifs sanctionnent la
     SOUFFRANCE (être touché, mourir). Un coup absorbé par le bouclier compte
     dans `hitsTaken` au même titre qu'un death-bomb : le bouclier a été touché,
     donc le joueur s'est mis en danger, donc le rang le remarque. */
  const perf =
    w.kills * UP_PER_KILL +
    w.grazeCount * UP_PER_GRAZE -
    w.progress.hitsTaken * DOWN_PER_HIT -
    w.progress.deaths * DOWN_PER_DEATH;

  // Variation depuis le tick précédent : c'est elle, et non le total, qui pousse
  // le curseur — voir l'en-tête.
  const delta = perf - run.rankPerf;
  run.rankPerf = perf;

  run.rankMul = clamp(run.rankMul + delta, RANK_MIN, RANK_MAX);
}
